import { useEffect, useState } from "react";
import { useLocation } from "wouter";
import { CheckCircle2, Loader2, Calendar, MapPin, Clock, Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { useOrder } from "@/hooks/use-orders";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { apiClient } from "@/lib/api-client";
import { API_URL } from "@/config/api";

async function downloadInvoice(orderId: string) {
  const token = apiClient.getToken();
  const url = `${API_URL}/orders/${orderId}/invoice`;

  try {
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error('Failed to download invoice');
    }

    const blob = await response.blob();
    const downloadUrl = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = `invoice-${orderId}.pdf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  } catch (error) {
    console.error('Error downloading invoice:', error);
  }
}

export default function PaymentSuccess() {
  const { t, i18n } = useTranslation();
  const isRtl = i18n.language === 'ar';
  const [, setLocation] = useLocation();
  const [downloading, setDownloading] = useState(false);
  
  const urlParams = new URLSearchParams(window.location.search);
  const orderId = urlParams.get('orderId');
  
  // Only fetch order if user is authenticated
  const isAuthenticated = !!apiClient.getToken();
  const { data: order, isLoading, isError } = useOrder(orderId || '');
  
  useEffect(() => {
    if (!orderId) {
      setLocation('/');
    }
  }, [orderId, setLocation]);

  if (isAuthenticated && isLoading) {
    return (
      <div className="min-h-screen pt-24 pb-24 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-24 flex items-center justify-center bg-background">
      <div className="container mx-auto px-4 max-w-lg">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h1 className="text-3xl font-bold mb-4 font-display">{t("paymentSuccess.title")}</h1>
          <p className="text-muted-foreground">
            {t("paymentSuccess.message")}
          </p>
        </div>

        {order && !isError && (
          <div className="bg-card rounded-xl p-6 border border-border mb-6">
            <h2 className="font-semibold text-lg mb-4">{t("paymentSuccess.orderDetails")}</h2>
            
            <div className="space-y-3">
              {order.items && order.items.length > 0 && (
                <div className="space-y-2">
                  {order.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm">
                      <span className="text-muted-foreground">
                        {item.serviceName} × {item.quantity}
                      </span>
                      <span className="font-medium">{item.subtotal.toFixed(3)} {t("services.currency")}</span>
                    </div>
                  ))}
                </div>
              )}
              
              {order.appointmentDateTime && (
                <div className="flex items-center gap-2 text-sm">
                  <Calendar className="w-4 h-4 text-muted-foreground" />
                  <span>
                    {format(new Date(order.appointmentDateTime), "PPP", { locale: isRtl ? ar : enUS })}
                  </span>
                </div>
              )}
              
              {order.appointmentTime && (
                <div className="flex items-center gap-2 text-sm">
                  <Clock className="w-4 h-4 text-muted-foreground" />
                  <span>{order.appointmentTime}</span>
                </div>
              )}
              
              {order.customerAddress && (
                <div className="flex items-center gap-2 text-sm">
                  <MapPin className="w-4 h-4 text-muted-foreground" />
                  <span>{order.customerAddress}</span>
                </div>
              )}
              
              <div className="border-t border-border pt-3 mt-3">
                <div className="flex justify-between items-center font-semibold">
                  <span>{t("paymentSuccess.totalPaid")}</span>
                  <span className="text-primary">{order.totalPrice?.toFixed(3)} {t("services.currency")}</span>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {orderId && (
          <p className="text-sm text-muted-foreground text-center mb-6">
            {t("orders.orderNumber")}: {orderId.slice(-8).toUpperCase()}
          </p>
        )}

        {orderId && (
          <Button
            variant="outline"
            className="w-full mb-3"
            onClick={async () => { setDownloading(true); await downloadInvoice(orderId); setDownloading(false); }}
            disabled={downloading}
            data-testid="button-download-invoice"
          >
            {downloading ? <Loader2 className="w-4 h-4 me-2 animate-spin" /> : <Download className="w-4 h-4 me-2" />}
            {downloading ? t("paymentSuccess.downloading") : t("paymentSuccess.downloadInvoice")}
          </Button>
        )}

        <div className="space-y-3">
          <Button 
            onClick={() => setLocation('/orders')} 
            className="w-full"
            data-testid="button-view-orders"
          >
            {t("paymentSuccess.viewOrders")}
          </Button>
          <Button 
            onClick={() => setLocation('/')} 
            variant="outline"
            className="w-full"
            data-testid="button-return-home"
          >
            {t("paymentSuccess.returnHome")}
          </Button>
        </div>
      </div>
    </div>
  );
}
